import { Component, createEffect, Index, JSX, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';

export interface FsPanelBarTab {
  icon: JSX.Element;
  /** @deprecated Adding the title creates a header element for the panel.
   * This is discouraged as the header content is not flexible / dynamic
   **/
  title: string;
  /** Optional tooltip text. If not provided, falls back to title. */
  tooltip?: string;
  content: Component;
  /** Unique identifier for the tab. Required for tab selection to work. */
  tabId: string;
  disabled?: boolean;
}
export interface FsPanelBarProps {
  activeTabId: string | undefined;
  onActiveTabChange(id: string | undefined): void;
  tabs: FsPanelBarTab[];
  side?: 'left' | 'right';
  activeTab?: FsPanelBarTab;
  panelId: string;
  onPanelToggled?(detail: { isOpen: boolean }): void;
  showTabs?: boolean;
}

export interface FsTabDetails {
  targetId: string;
  tabId: string;
  close?: boolean;
}

/**
 * @deprecated Use `VerticalTabPanel` from `@trimble/ui-components/solidjs` for new side panel bars.
 */
export const FsPanelBar: Component<FsPanelBarProps> = (props) => {
  // Derive activeTab from activeTabId and current tabs list
  // This ensures activeTab always references the current tab object, not a stale one
  const activeTab = (): FsPanelBarTab | undefined => {
    const currentId = props.activeTabId;
    if (!currentId) return undefined;
    return props.tabs.find((t) => t.tabId === currentId);
  };

  const isActiveTab = (tab: FsPanelBarTab): boolean => {
    return tab.tabId === props.activeTabId;
  };

  const handleTabClick = (tab: FsPanelBarTab): void => {
    if (isActiveTab(tab)) {
      props.onActiveTabChange(undefined);
      return;
    }
    // Close any currently active tab. Without this, clicking a different tab
    // would not close the currently active tab.
    props.onActiveTabChange(undefined);
    // Set the clicked tab as active
    props.onActiveTabChange(tab.tabId);
  };

  createEffect(() => {
    if (activeTab()) {
      props.onPanelToggled?.({ isOpen: true });
    } else {
      props.onPanelToggled?.({ isOpen: false });
    }
  });

  return (
    <div
      class={`h-full flex items-center relative bg-base-100`}
      data-testid={props.panelId}
      id={props.panelId}
    >
      <Show when={props.showTabs ?? true}>
        <div class="flex flex-col overflow-clip rounded-sm">
          <Index each={props.tabs.filter((tab) => !tab.disabled)}>
            {(tab) => {
              return (
                <modus-tooltip
                  class="z-8 w-full"
                  text={tab().tooltip ?? tab().title}
                  position={props.side}
                >
                  <button
                    data-testid={`${tab().tabId}-button`}
                    data-active={isActiveTab(tab()) ? 'true' : 'false'}
                    class={`flex w-full flex-col items-center justify-center text-base-content hover:bg-base-page p-2 ${
                      isActiveTab(tab()) ? 'bg-base-page' : ''
                    }`}
                    onClick={() => handleTabClick(tab())}
                    button-style="borderless"
                  >
                    {tab().icon}
                  </button>
                </modus-tooltip>
              );
            }}
          </Index>
        </div>
      </Show>
      <Show when={activeTab()}>
        {(tab) => (
          <div
            data-testid="panel-content-container"
            class={`flex flex-col absolute h-full z-[5] ${
              props.side === 'right' ? 'right-full' : 'right-0 left-full'
            } w-80 bg-base-page`}
          >
            <Show when={tab().title}>
              <div class="min-h-[65px] text-[1.375rem] h-[65px] flex items-center p-2 font-semibold text-base-content border-b border-base-200">
                {tab().title}
              </div>
            </Show>
            <div data-testid="panel-content" class="flex-grow overflow-auto">
              <Dynamic component={tab().content} />
            </div>
          </div>
        )}
      </Show>
    </div>
  );
};
